type ScrollSnapshot = {
  htmlOverflow: string
  bodyOverflow: string
  htmlPaddingRight: string
}

const SCROLL_KEYS = new Set([
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "PageUp",
  "PageDown",
  "Home",
  "End",
  " ",
])

function pathHas(event: Event, className: string): boolean {
  return event.composedPath().some(
    (node) => node instanceof Element && node.classList.contains(className)
  )
}

function isTypingTarget(event: Event): boolean {
  const target = event.composedPath()[0]
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  )
}

function isActivationTarget(event: Event): boolean {
  const target = event.composedPath()[0]
  return target instanceof HTMLButtonElement || target instanceof HTMLAnchorElement
}

/**
 * Stops the host page from scrolling while the lightbox is open.
 *
 * Same approach as the app lightbox: `overflow: hidden` only. Pinning the body
 * with `position: fixed` pulls the page out of flow and shifts layout (the
 * scrollbar gutter, sticky headers, and `width: 100%` all jump). Overlay
 * scrollbars, which marketing sites on macOS use, have no gutter to pad, so
 * that pin is a visible shift for no benefit. A real classic scrollbar is
 * compensated on the document element so the content width stays put.
 */
export function lockPageScroll(): () => void {
  const html = document.documentElement
  const body = document.body
  const snapshot: ScrollSnapshot = {
    htmlOverflow: html.style.overflow,
    bodyOverflow: body.style.overflow,
    htmlPaddingRight: html.style.paddingRight,
  }

  const scrollbarGap = Math.max(0, window.innerWidth - html.clientWidth)

  html.style.overflow = "hidden"
  body.style.overflow = "hidden"
  if (scrollbarGap > 0) {
    const existingPadding = Number.parseFloat(getComputedStyle(html).paddingRight) || 0
    html.style.paddingRight = `${existingPadding + scrollbarGap}px`
  }

  const stopBackgroundScroll = (event: Event) => {
    if (pathHas(event, "bpp-results")) {
      return
    }
    event.preventDefault()
  }

  const stopBackgroundKeys = (event: KeyboardEvent) => {
    if (!SCROLL_KEYS.has(event.key) || isTypingTarget(event) || pathHas(event, "bpp-results")) {
      return
    }
    // Space activates the focused button. Blocking it breaks Close.
    if (event.key === " " && isActivationTarget(event)) {
      return
    }
    event.preventDefault()
  }

  const scrollListener: AddEventListenerOptions = { capture: true, passive: false }
  window.addEventListener("wheel", stopBackgroundScroll, scrollListener)
  window.addEventListener("touchmove", stopBackgroundScroll, scrollListener)
  window.addEventListener("keydown", stopBackgroundKeys, true)

  return () => {
    html.style.overflow = snapshot.htmlOverflow
    body.style.overflow = snapshot.bodyOverflow
    html.style.paddingRight = snapshot.htmlPaddingRight
    window.removeEventListener("wheel", stopBackgroundScroll, scrollListener)
    window.removeEventListener("touchmove", stopBackgroundScroll, scrollListener)
    window.removeEventListener("keydown", stopBackgroundKeys, true)
  }
}
